import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useRepeatedBreakdownAssets } from "@/features/assets/reliability";
import { formatDuration } from "@/features/work-orders/format";
import { formatDate } from "@/features/assets/format";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/repeated-breakdown-assets")({
  component: RepeatedBreakdownAssetsPage,
});

function RepeatedBreakdownAssetsPage() {
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const branchId = lockedBranchId ?? currentBranchId ?? null;

  const [minCount, setMinCount] = useState(2);
  const [days, setDays] = useState(30);
  const from = useMemo(() => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(), [days]);

  const { data, isLoading } = useRepeatedBreakdownAssets({ branchId, from, minCount });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Repeated Breakdown Assets</h1>
        <p className="text-sm text-muted-foreground">
          Assets with {minCount} or more valid breakdown work orders in the last {days} days.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1">
            <Label className="text-xs">Period (days)</Label>
            <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))} className="w-28" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Min breakdowns</Label>
            <Input type="number" min={2} max={20} value={minCount} onChange={(e) => setMinCount(Math.max(2, Number(e.target.value) || 2))} className="w-28" />
          </div>
          <div className="ml-auto text-sm text-muted-foreground">
            {isLoading ? "Loading…" : `${data?.length ?? 0} assets`}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset Code</TableHead>
                  <TableHead>Asset Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Breakdowns</TableHead>
                  <TableHead className="text-right">Total Downtime</TableHead>
                  <TableHead>Last Breakdown</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data ?? []).map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell className="font-mono text-xs">{r.asset?.asset_code ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.asset?.name ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.asset?.asset_class ?? r.asset?.asset_type ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.asset?.branch?.name ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.asset?.department?.name ?? "—"}</TableCell>
                    <TableCell className="text-xs">{r.asset?.location?.name ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant="secondary">{r.breakdown_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatDuration(r.total_downtime_minutes)}</TableCell>
                    <TableCell className="text-xs">{formatDate(r.last_breakdown_at)}</TableCell>
                    <TableCell className="text-xs">{r.asset?.status ?? "—"}</TableCell>
                    <TableCell>
                      <Button asChild size="sm" variant="outline">
                        <Link to="/assets/$assetId" params={{ assetId: r.asset_id }}>Open</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!data || data.length === 0) && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={11} className="py-8 text-center text-sm text-muted-foreground">
                      No repeated-breakdown assets in the selected window.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
